import { Box, Button, CircularProgress } from '@mui/material';
import { AxiosResponse } from 'axios';
import { Dayjs } from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import Collapsible from 'components/collapsible';
import LoadingSpinner from 'components/loading-spinner';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import ListFeeders from 'views/production-systems/features/farm-setup/components/list-feeders';
import RegistrationHeader from '../../../components/registration-header';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { getFeederRegistrationLog, putFeederRegistrationLog } from 'views/production-systems/api/productionSystems';
import { usePondsState } from 'views/production-systems/context/ponds-context';

import {
  PondFeederAssigments,
  UpdateAssignedFeeders,
  UpdateAssignedFeedersInput,
  UpdateFeedersInput,
  UpdateFeedersInputForm,
} from 'views/production-systems/features/farm-setup/types/update-feeders-input.type';

import _ from 'underscore';
import { FeedersProvider } from 'views/self-service/features/feeders/context/feeders-context';

type Props = {
  logId: number;
  handleClose: () => void;
  canEdit: boolean;
  dateCreated: Dayjs;
  eventNr: number;
};

type ParamTypes = {
  globalSiteId: string;
};

const EditFeeder: React.FC<Props> = ({ handleClose, logId, canEdit = true, dateCreated }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();

  const [confirm, setConfirm] = useState<boolean>(false);
  const { lockedPonds, globalSiteId, lockPonds, releasePonds } = usePondsState();

  //#region FETCH DATA
  const { data: feederAssignments, isFetching } = useGet(
    ['getFeeder', globalSiteId, logId],
    ({ queryKey }) => getFeederRegistrationLog(String(queryKey[1]), Number(queryKey[2]))
  );

  useEffect(() => {
    if (feederAssignments && canEdit) lockPonds(feederAssignments.pondFeederAssignments.map((pond) => pond.pondId));
  }, [canEdit, feederAssignments, lockPonds]);
  //#endregion FETCH DATA

  // #region HOOKS
  const pondsWithFeeders = useMemo(() => {
    return _.chain(feederAssignments?.pondFeederAssignments)
      .sortBy('pondName')
      .value()
      .map((pond) => {
        return {
          pondId: pond.pondId,
          pondName: pond.pondName,
          pondCurrentBiomass: pond.pondCurrentBiomass,
          assignedFeeders: pond.assignedFeeders.map((feeder) => {
            return {
              feederId: feeder.feederId,
              feederName: feeder.feederName,
              storageCapacity: feeder.storageCapacity,
              feedingCapacity: feeder.feedingCapacity,
              numberOfFeeders: feeder.quantity,
              feederRegDate: '',
              isDeleted: feeder.quantity === 0,
            };
          }),
        };
      });
  }, [feederAssignments]);

  // #endregion

  // #region FORMS
  const defaultValues = {
    ponds: pondsWithFeeders,
  };

  const form = useForm<UpdateFeedersInputForm>({
    defaultValues: defaultValues,
    shouldUnregister: false,
    mode: 'onChange',
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = form;

  const { fields: rows } = useFieldArray({
    control: control,
    name: `ponds`,
  });

  const watchPonds = useWatch({ control, name: 'ponds' }) || [];
  // #endregion

  //#region POST DATA
  const { isPending: isAssignLoading, mutate: assignFeedersToPonds } = usePost<
    AxiosResponse,
    { globalSiteId: string; request: UpdateAssignedFeedersInput }
  >(
    (request) => putFeederRegistrationLog(request.globalSiteId, logId, request.request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      feederAssignments && releasePonds(feederAssignments.pondFeederAssignments.map((pond) => pond.pondId));
      queryClient.invalidateQueries({
        queryKey: ['getRegistrations']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion POST DATA

  // #region HANDLERS
  const handleSubmitFeedersData = (request: { ponds: UpdateFeedersInput[] }) => {
    const assignments: PondFeederAssigments[] = request.ponds
      .map((pond) => {
        const feedersForPond = pond.assignedFeeders.reduce((acc: UpdateAssignedFeeders[], feeder) => {
          if (!feeder.isDeleted) {
            acc.push({
              feederId: feeder.feederId,
              quantity: Number(feeder.numberOfFeeders),
            });
          } else if (feeder.isDeleted && !feeder.isNew) {
            acc.push({
              feederId: feeder.feederId,
              quantity: 0,
            });
          }
          return acc;
        }, []);

        return {
          pondId: pond.pondId,
          assignedFeeders: feedersForPond,
        };
      })
      .filter((pond) => pond.assignedFeeders.length > 0)
      .map((pond) => {
        return {
          pondId: pond.pondId,
          assignedFeeders: pond.assignedFeeders.map((feeder) => ({ feederId: feeder.feederId, quantity: Number(feeder.quantity) })),
        } as PondFeederAssigments;
      });

    const pondsWithAssignments: UpdateAssignedFeedersInput = {
      date: dateCreated.format('YYYY-MM-DD'),
      pondFeederAssignments: assignments,
    };

    if (pondsWithAssignments.pondFeederAssignments.length === 0) return;

    assignFeedersToPonds({ globalSiteId: params.globalSiteId, request: pondsWithAssignments });
  };

  const isDisableSubmit = watchPonds
    .flatMap((pond) => pond.assignedFeeders || [])
    .every((aerator) => aerator.isNew && aerator.isDeleted);
  // #endregion HANDLERS

  useEffect(() => {
    reset({ ponds: pondsWithFeeders });
  }, [pondsWithFeeders]);

  return isFetching ? (
    <LoadingSpinner />
  ) : lockedPonds.notGranted.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} />
  ) : (
    <>
      <PondLockTimer handleDialogClose={handleClose} />
      <RegistrationHeader dateCreated={dateCreated} />
      <FormProvider {...form}>
        <form onSubmit={handleSubmit((data) => handleSubmitFeedersData(data))}>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            {rows.map((pond, idx) => {
              const { pondCurrentBiomass, pondName } = pond;
              return (
                <Collapsible hideHeaderBorder label={`${pondName}`} open={idx === 0}>
                  <ListFeeders fieldIndex={idx} pondCurrentBiomass={pondCurrentBiomass} />
                </Collapsible>
              );
            })}
            <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
              <Button
                type="submit"
                disabled={isAssignLoading || !isValid || !isDirty || isDisableSubmit}
                startIcon={isAssignLoading ? <CircularProgress color="inherit" size={18} /> : null}>
                <FormattedMessage id="general.submit" values={{ type: '' }} />
              </Button>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </>
  );
};

const ManageEditFeedersContainer: React.FC<Props> = ({ handleClose, logId, eventNr, canEdit = true, dateCreated }) => {
  return (
    <FeedersProvider>
      <EditFeeder handleClose={handleClose} logId={logId} eventNr={eventNr} canEdit={canEdit} dateCreated={dateCreated} />
    </FeedersProvider>
  );
};

export default React.memo(ManageEditFeedersContainer);
